import { createHttpOwnerListsApi } from "../../api/ownerListsApi";
import { useShopPage } from "./useShopPage";
import { useShopAction } from "./useShopAction";
import { ProductListSkeleton, ShopActivity, ShopLoadError, ShopMore } from "./ShopLoading";
import { useMemo, useState } from "react";
import { ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { categoryProductIds } from "@stackje/site-builder";
import type { ShopApi, ShopIndex } from "../../api/contract";
import { moveCategory, newShopId, productCount, slugFromName } from "../../lib/shop";
import { AreaError, Card, CheckRow, Columns, ConfirmDialog, EmptyState, Field, ListHead, SaveBar } from "../area";

type Category = ShopIndex["categories"][number];

/** The address a new category used to get from its id; it follows the name like an empty one. */
const PLACEHOLDER_SLUG = /^category-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** A web address not used by another category: "mugs", else "mugs-2", "mugs-3"… */
function freeSlug(name: string, categories: Category[], self?: string): string {
  const base = slugFromName(name) || "category";
  const taken = new Set(categories.filter((item) => item.id !== self).map((item) => item.slug));
  let slug = base;
  for (let n = 2; taken.has(slug); n += 1) slug = `${base}-${n}`;
  return slug;
}

const inputOf = (category: Category, change: Partial<Category> = {}) => ({
  name: category.name, slug: category.slug, description: category.description, parentId: category.parentId, position: category.position, showInMenu: category.showInMenu, ...change,
});

/** The shop menu in order, with the arrows that move a category up or down. */
function CategoryList({ shop, id, busy, onSelect, onMove }: { shop: ShopIndex; id: string | null; busy: boolean; onSelect: (id: string) => void; onMove: (id: string, step: -1 | 1) => void }) {
  const ordered = [...shop.categories].sort((a, b) => a.position - b.position);
  return <Card title="Shop menu" description="Use the arrows to set the order">
    <ul className="bw-shop-categories">
      {ordered.map((item, index) => <li key={item.id} aria-current={id === item.id ? "true" : undefined}>
        <button type="button" className="bw-shop-category" disabled={busy} onClick={() => onSelect(item.id)}>
          <span>{item.parentId && "↳ "}{item.name}</span>
          <small>{productCount(shop.categoryCounts?.[item.id] ?? categoryProductIds(shop, item.id, true).length)}</small>
        </button>
        <span className="bw-shop-category-moves">
          <button type="button" className="bw-btn bw-btn--icon" aria-label={`Move ${item.name} up`} disabled={busy || index === 0} onClick={() => onMove(item.id, -1)}><ChevronUp size={16} aria-hidden="true" /></button>
          <button type="button" className="bw-btn bw-btn--icon" aria-label={`Move ${item.name} down`} disabled={busy || index === ordered.length - 1} onClick={() => onMove(item.id, 1)}><ChevronDown size={16} aria-hidden="true" /></button>
        </span>
      </li>)}
    </ul>
  </Card>;
}

/** One category's details, the products in it, and Delete. */
function CategoryForm({ api, siteId, shop, category, busy, previewRoot, onSave, onDelete }: { api: ShopApi; siteId: string; shop: ShopIndex; category: Category; busy: boolean; previewRoot?: string; onSave: (data: FormData) => void; onDelete: () => void }) {
  const lists = useMemo(() => api.lists ?? createHttpOwnerListsApi(), [api]);
  const [name, setName] = useState(category.name);
  // The web address follows the name until the owner types their own; clearing it goes back to following.
  const [ownSlug, setOwnSlug] = useState(category.slug !== freeSlug(category.name, shop.categories, category.id) && !PLACEHOLDER_SLUG.test(category.slug) ? category.slug : "");
  const slug = ownSlug.trim() || freeSlug(name, shop.categories, category.id);
  const view = previewRoot && <a className="bw-area-card-link" href={`${previewRoot}${shop.settings!.base}/c/${category.slug}`} target="_blank" rel="noreferrer">View page <ArrowUpRight size={14} aria-hidden="true" /></a>;
  const page = useShopPage(lists.products, siteId, { category: category.id }, shop);
  return <form aria-busy={busy} onSubmit={(event) => { event.preventDefault(); if (!busy) onSave(new FormData(event.currentTarget)); }}>
    <fieldset disabled={busy} className="bw-shop-locked"><Card title={category.name} actions={view}>
      <Field label="Name"><input name="name" required value={name} onChange={(event) => setName(event.target.value)} /></Field>
      <Field label="Inside">
        <select name="parent" defaultValue={category.parentId ?? ""}>
          <option value="">Nothing (top level)</option>
          {shop.categories.filter((item) => item.id !== category.id).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </Field>
      <Field label="Web address" hint={ownSlug.trim() ? "Clear it to follow the name again." : "Follows the name. Type your own to keep it."}><span className="bw-area-prefixed"><span>{shop.settings!.base}/c/</span><input value={ownSlug} placeholder={slug} onChange={(event) => setOwnSlug(event.target.value)} /></span><input type="hidden" name="slug" value={slug} /></Field>
      <Field label="Description"><textarea name="description" defaultValue={category.description} /></Field>
      <CheckRow name="menu" defaultChecked={category.showInMenu} label="Show in the shop menu" />
      <h3>{productCount(shop.categoryCounts?.[category.id] ?? page.total)} · includes subcategories</h3>
      {page.rows.length > 0 && <ul className="bw-shop-plain-list">{page.rows.map(row => <li key={row.id}>{row.name}</li>)}</ul>}
      {page.busy && (page.rows.length ? <ShopActivity announce={false} label="Loading products…" /> : <ProductListSkeleton />)}
      <ShopMore shown={page.rows.length} total={page.total} hasMore={page.hasMore} busy={page.busy} onMore={page.more} />
      {page.error && <ShopLoadError message={page.error} retry={page.retry} />}
      <p>A product joins a category from its own Details. Deleting keeps every product; move or remove subcategories first.</p>
      <div className="bw-actions">
        <button className="bw-btn bw-btn--primary bw-btn--action" disabled={busy}>{busy ? <ShopActivity announce={false} label="Saving…" /> : "Save"}</button>
        <button type="button" className="bw-btn bw-btn--quiet" onClick={onDelete}>Delete category</button>
      </div>
    </Card>
  </fieldset></form>;
}

/** The Categories screen: the shop menu on the left, the picked category on the right. */
export function CategoriesView({ api, siteId, shop, onSaved, previewRoot }: { previewRoot?: string; api: ShopApi; siteId: string; shop: ShopIndex; onSaved: (next: ShopIndex) => void }) {
  const [id, setId] = useState<string | null>(shop.categories[0]?.id ?? null);
  const { busy, error, setError, run } = useShopAction();
  const [undo, setUndo] = useState<Category | null>(null);
  const [confirm, setConfirm] = useState(false);
  const category = shop.categories.find((item) => item.id === id);

  /** Runs a save and hands the new catalogue to the screen. True when it went through. */
  const save = async (action: () => Promise<ShopIndex>): Promise<boolean> => {
    return run(async () => { onSaved(await action()); });
  };
  const create = () => {
    const nextId = newShopId("category");
    void save(async () => {
      const next = await api.saveShopCategory(siteId, nextId, 0, { name: "New category", slug: freeSlug("New category", shop.categories), parentId: null, description: "", position: shop.categories.length, showInMenu: true });
      setId(nextId);
      return next;
    });
  };
  const move = (categoryId: string, step: -1 | 1) => {
    const changes = moveCategory(shop.categories, categoryId, step);
    if (!changes) return;
    void save(async () => {
      let next = shop;
      for (const change of changes) {
        const row = shop.categories.find((item) => item.id === change.id)!;
        next = await api.saveShopCategory(siteId, row.id, row.rowVersion, inputOf(row, { position: change.position }));
      }
      return next;
    });
  };
  const submit = (data: FormData) => {
    if (!category) return;
    void save(() => api.saveShopCategory(siteId, category.id, category.rowVersion, { name: String(data.get("name")), slug: String(data.get("slug")), description: String(data.get("description")), parentId: String(data.get("parent")) || null, position: category.position, showInMenu: data.has("menu") })).then((saved) => { if (saved) setUndo(category); });
  };
  const undoSave = () => {
    const current = undo && shop.categories.find((item) => item.id === undo.id);
    if (!undo || !current) { setUndo(null); return; }
    void save(() => api.saveShopCategory(siteId, current.id, current.rowVersion, inputOf(undo))).then((saved) => { if (saved) setUndo(null); });
  };
  const remove = () => {
    if (!category) return;
    void save(async () => {
      const next = await api.deleteShopCategory(siteId, category.id, category.rowVersion);
      setId(null);
      setConfirm(false);
      return next;
    });
  };

  return <>
    <ListHead title="Categories" action={{ label: "New category", onClick: create, disabled: busy }} />
    {shop.categories.length === 0 ? <EmptyState title="No categories yet">Categories group products in the shop menu. Add one, then pick it from a product's Details.</EmptyState> : <Columns ratio="side-main">
      <div><CategoryList shop={shop} id={id} busy={busy} onSelect={(next) => { setId(next); setConfirm(false); }} onMove={move} /></div>
      <div>{category && <CategoryForm api={api} siteId={siteId} key={`${category.id}-${category.rowVersion}`} shop={shop} category={category} busy={busy} previewRoot={previewRoot} onSave={submit} onDelete={() => setConfirm(true)} />}</div>
    </Columns>}
    {confirm && category && <ConfirmDialog title={`Delete “${category.name}”?`} confirmLabel={busy ? "Working…" : "Delete category"} cancelLabel="Keep" tone="danger" busy={busy} onCancel={() => { if (!busy) setConfirm(false); }} onConfirm={remove}>
      {busy && <ShopActivity label="Working…" />}<p className="bw-area-dialog-lead">Products keep their other categories.</p>
    </ConfirmDialog>}
    {error && <AreaError message={error} onDismiss={() => setError(null)} />}
    {busy && !confirm && <ShopActivity label="Saving category…" />}
    {undo && <SaveBar state="saved" busy={busy} onUndo={undoSave} />}
  </>;
}
